import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getAdminStats, checkIsAdmin } from "@/lib/admin.functions";
import { sendAdminNotificationTests } from "@/lib/admin-test.functions";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Globe, Users, BarChart3, Target, Gift, Search, TrendingUp, ShieldCheck, DollarSign, Clock, Trophy, MessageSquare, Mail, LifeBuoy, AlertCircle, Link as LinkIcon } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { ReferralsLeaderboard } from "@/components/admin/ReferralsLeaderboard";
import { AdminEdgeErrorsTab } from "@/components/admin/AdminEdgeErrorsTab";
import { BookingUrlReviewTab } from "@/components/admin/BookingUrlReviewTab";
import { AdminStarterKitOrdersTab } from "@/components/admin/AdminStarterKitOrdersTab";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminPage,
});

function AdminPage() {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  

  useEffect(() => {
    if (!user) return;
    const token = (supabase as any)._supabase?.auth
      ? undefined
      : undefined;

    async function load() {
      try {
        const session = await supabase.auth.getSession();
        const accessToken = session.data.session?.access_token;
        if (!accessToken) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        const adminCheck = await checkIsAdmin({
          data: undefined as any,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!adminCheck.isAdmin) {
          setIsAdmin(false);
          setLoading(false);
          return;
        }
        setIsAdmin(true);

        const data = await getAdminStats({
          data: undefined as any,
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        setStats(data);
      } catch {
        setIsAdmin(false);
      }
      setLoading(false);
    }
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading admin…</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-2">
          <ShieldCheck className="size-12 mx-auto text-muted-foreground/40" />
          <h1 className="text-xl font-semibold">Access Denied</h1>
          <p className="text-sm text-muted-foreground">You don't have admin access.</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return <AdminDashboard stats={stats} search={search} setSearch={setSearch} />;
}

function AdminDashboard({ stats, search, setSearch }: { stats: any; search: string; setSearch: (v: string) => void }) {
  const { merchants, events, feedback, scansByCountry, scansByCity, referrals = [], productFeedback = [], edgeErrors = [] } = stats;
  const [testEmailState, setTestEmailState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const recentErrorCount = useMemo(() => {
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    return edgeErrors.filter((e: any) => new Date(e.created_at).getTime() >= oneHourAgo).length;
  }, [edgeErrors]);

  const pendingBookingUrlCount = useMemo(
    () => merchants.filter((m: any) => m.booking_url && m.booking_url_status === "pending").length,
    [merchants]
  );

  const totalScans = events.filter((e: any) => e.event_type === "scan").length;
  const totalEmails = events.filter((e: any) => e.event_type === "email_submitted").length;
  const totalRedemptions = feedback.filter((f: any) => f.status === "redeemed").length;
  const totalFeedback = feedback.length;

  // Approximate FX rates to USD
  const FX_TO_USD: Record<string, number> = { SEK: 0.095, NOK: 0.09, DKK: 0.14, EUR: 1.08, GBP: 1.27, USD: 1 };

  // Revenue Multiplier: sum of (redeemed count per merchant * merchant avg_order_value), converted to USD
  const estRevenue = useMemo(() => {
    const redeemedByMerchant: Record<string, number> = {};
    feedback.forEach((f: any) => {
      if (f.status === "redeemed") {
        redeemedByMerchant[f.merchant_id] = (redeemedByMerchant[f.merchant_id] || 0) + 1;
      }
    });
    let total = 0;
    merchants.forEach((m: any) => {
      const count = redeemedByMerchant[m.id] || 0;
      const localRevenue = count * (m.avg_order_value / 100); // minor units to major
      const fx = FX_TO_USD[m.currency || "SEK"] || 0.095;
      total += localRevenue * fx;
    });
    return total;
  }, [feedback, merchants]);

  // Country breakdown
  const countryMap = useMemo(() => {
    const map: Record<string, number> = {};
    scansByCountry.forEach((e: any) => {
      if (e.country_code) map[e.country_code] = (map[e.country_code] || 0) + 1;
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20);
  }, [scansByCountry]);

  // City breakdown
  const cityMap = useMemo(() => {
    const map: Record<string, number> = {};
    scansByCity.forEach((e: any) => {
      if (e.city) {
        const key = `${e.city}, ${e.country_code || "??"}`;
        map[key] = (map[key] || 0) + 1;
      }
    });
    return Object.entries(map)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 20);
  }, [scansByCity]);

  // Business type breakdown
  const typeBreakdown = useMemo(() => {
    const map: Record<string, number> = {};
    merchants.forEach((m: any) => {
      const t = m.business_type || "unknown";
      map[t] = (map[t] || 0) + 1;
    });
    return Object.entries(map).sort(([, a], [, b]) => b - a);
  }, [merchants]);

  // Onboarding completion rate
  const onboardedCount = merchants.filter((m: any) => m.onboarding_completed).length;
  const onboardingRate = merchants.length ? ((onboardedCount / merchants.length) * 100).toFixed(1) : "0";

  // Scan-to-submit conversion
  const scanToSubmitRate = totalScans ? ((totalEmails / totalScans) * 100).toFixed(1) : "0";

  // Avg star rating
  const avgRating = totalFeedback
    ? (feedback.reduce((sum: number, f: any) => sum + f.star_rating, 0) / totalFeedback).toFixed(1)
    : "—";

  // Redemption rate
  const redemptionRate = totalFeedback ? ((totalRedemptions / totalFeedback) * 100).toFixed(1) : "0";

  // Feedback length
  const longFeedback = feedback.filter((f: any) => f.customer_comment && f.customer_comment.length >= 20).length;
  const shortFeedback = feedback.filter((f: any) => f.customer_comment && f.customer_comment.length > 0 && f.customer_comment.length < 20).length;

  // Print intent
  const posterDownloads = events.filter((e: any) => e.event_type === "poster_downloaded").length;
  const cardDownloads = events.filter((e: any) => e.event_type === "card_downloaded").length;

  // Avg Time to Activate
  const avgTimeToActivate = useMemo(() => {
    const firstScanByMerchant: Record<string, string> = {};
    events
      .filter((e: any) => e.event_type === "scan" && e.merchant_id)
      .forEach((e: any) => {
        if (!firstScanByMerchant[e.merchant_id] || e.created_at < firstScanByMerchant[e.merchant_id]) {
          firstScanByMerchant[e.merchant_id] = e.created_at;
        }
      });

    const deltas: number[] = [];
    merchants.forEach((m: any) => {
      if (m.onboarding_completed_at && firstScanByMerchant[m.id]) {
        const diff = new Date(firstScanByMerchant[m.id]).getTime() - new Date(m.onboarding_completed_at).getTime();
        if (diff > 0) deltas.push(diff);
      }
    });

    if (deltas.length === 0) return { label: "—", count: 0 };
    const avgMs = deltas.reduce((a, b) => a + b, 0) / deltas.length;
    const avgHours = avgMs / (1000 * 60 * 60);
    const label = avgHours >= 24 ? `${(avgHours / 24).toFixed(1)} days` : `${avgHours.toFixed(1)} hours`;
    return { label, count: deltas.length };
  }, [events, merchants]);

  // Code Cascade: Redeemed C2 without C1
  const codeCascade = useMemo(() => {
    // Group feedback by customer_email + merchant_id
    const groups: Record<string, any[]> = {};
    feedback.forEach((f: any) => {
      const key = `${f.customer_email}__${f.merchant_id}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    });

    let c2WithoutC1 = 0;
    let multiCodeCustomers = 0;
    Object.values(groups).forEach((entries) => {
      if (entries.length < 2) return;
      // Sort by created_at ascending
      entries.sort((a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      multiCodeCustomers++;
      const c1 = entries[0];
      const c2 = entries[1];
      if (c1.status !== "redeemed" && c2.status === "redeemed") {
        c2WithoutC1++;
      }
    });

    return { c2WithoutC1, multiCodeCustomers };
  }, [feedback]);

  // Dormant merchants (onboarded, 0 scans)
  const merchantScanCounts = useMemo(() => {
    const map: Record<string, number> = {};
    events.filter((e: any) => e.event_type === "scan").forEach((e: any) => {
      if (e.merchant_id) map[e.merchant_id] = (map[e.merchant_id] || 0) + 1;
    });
    return map;
  }, [events]);

  const dormantMerchants = merchants.filter((m: any) => {
    if (!m.onboarding_completed) return false;
    return !merchantScanCounts[m.id];
  });

  // Filtered merchants for table
  const filteredMerchants = useMemo(() => {
    if (!search) return merchants;
    const q = search.toLowerCase();
    return merchants.filter((m: any) =>
      m.business_name?.toLowerCase().includes(q) ||
      m.slug?.toLowerCase().includes(q) ||
      m.business_type?.toLowerCase().includes(q)
    );
  }, [merchants, search]);

  // Per-merchant stats for table
  const merchantStats = useMemo(() => {
    const feedbackByMerchant: Record<string, any[]> = {};
    feedback.forEach((f: any) => {
      if (!feedbackByMerchant[f.merchant_id]) feedbackByMerchant[f.merchant_id] = [];
      feedbackByMerchant[f.merchant_id].push(f);
    });
    return { feedbackByMerchant, scanCounts: merchantScanCounts };
  }, [feedback, merchantScanCounts]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1400px] mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-primary" />
          <h1 className="text-xl font-display font-bold">Admin Dashboard</h1>
          <Link
            to="/admin/email-preview"
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5"
          >
            <Mail size={14} /> Email test bench
          </Link>
          <Link
            to="/admin/usage-alerts"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground border border-border rounded-md px-3 py-1.5"
          >
            <Mail size={14} /> Usage alerts
          </Link>

        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KPICard icon={<Users className="size-4" />} label="Merchants" value={merchants.length} />
          <KPICard icon={<Globe className="size-4" />} label="Total Scans" value={totalScans} />
          <KPICard icon={<Target className="size-4" />} label="Emails Captured" value={totalEmails} />
          <KPICard icon={<Gift className="size-4" />} label="Codes Redeemed" value={totalRedemptions} />
          <KPICard icon={<DollarSign className="size-4" />} label="Est. Revenue (USD)" value={`$${estRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} />
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="geo">Geo / Scans</TabsTrigger>
            <TabsTrigger value="activation">Activation</TabsTrigger>
            <TabsTrigger value="customer">Customer Funnel</TabsTrigger>
            <TabsTrigger value="rewards">Reward Loop</TabsTrigger>
            <TabsTrigger value="referrals" className="gap-1"><Trophy className="size-3" />Referrals</TabsTrigger>
            <TabsTrigger value="feedback" className="gap-1"><MessageSquare className="size-3" />Product Feedback</TabsTrigger>
            <TabsTrigger value="merchants">Merchants</TabsTrigger>
            <TabsTrigger value="support" className="gap-1"><LifeBuoy className="size-3" />Support inbox</TabsTrigger>
            <TabsTrigger value="booking-urls" className="gap-1">
              <LinkIcon className={`size-3 ${pendingBookingUrlCount > 0 ? "text-tack-amber" : ""}`} />
              Booking URLs{pendingBookingUrlCount > 0 ? ` (${pendingBookingUrlCount})` : ""}
            </TabsTrigger>
            <TabsTrigger value="starter-kits">Starter Kits</TabsTrigger>
            <TabsTrigger value="errors" className="gap-1">
              <AlertCircle className={`size-3 ${recentErrorCount > 0 ? "text-destructive" : ""}`} />
              Errors{recentErrorCount > 0 ? ` (${recentErrorCount})` : ""}
            </TabsTrigger>
          </TabsList>



          <TabsContent value="booking-urls">
            <BookingUrlReviewTab merchants={merchants as any} />
          </TabsContent>

          <TabsContent value="starter-kits">
            <AdminStarterKitOrdersTab />
          </TabsContent>



          <TabsContent value="errors">
            <AdminEdgeErrorsTab errors={edgeErrors} />
          </TabsContent>

          <TabsContent value="support">
            <SupportInboxChecklist />
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralsLeaderboard referrals={referrals} merchants={merchants} />
          </TabsContent>

          <TabsContent value="feedback">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <MessageSquare className="size-4" /> Product feedback responses ({productFeedback.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {productFeedback.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No responses yet. Merchants are asked once they hit 100 lifetime scans (or 60+ days with 10+ scans).</p>
                ) : (
                  <div className="space-y-3">
                    {productFeedback.map((r: any) => {
                      const m = merchants.find((x: any) => x.id === r.merchant_id);
                      return (
                        <div key={r.id} id={`feedback-${r.id}`} className="border-b border-border/50 last:border-0 pb-3 last:pb-0 scroll-mt-20 target:bg-primary/5 target:rounded-md target:px-2 target:py-2 target:-mx-2">
                          <div className="flex items-center gap-2 mb-1.5 text-xs text-muted-foreground flex-wrap">
                            <span className="font-medium text-foreground">{m?.business_name || "Unknown merchant"}</span>
                            <span>•</span>
                            <span>{m?.business_type || "—"}</span>
                            <span>•</span>
                            <span>{new Date(r.created_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <span className={r.prompt_id ? "text-primary" : "text-muted-foreground"}>
                              {r.prompt_id ? "Prompted" : "Volunteered"}
                            </span>
                          </div>
                          <p className="text-sm whitespace-pre-wrap">{r.response_text}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Overview */}
          <TabsContent value="overview">
            <Card className="mb-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mail className="size-4" />Internal notification emails
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={testEmailState === "sending"}
                  onClick={async () => {
                    setTestEmailState("sending");
                    try {
                      await sendAdminNotificationTests();
                      setTestEmailState("sent");
                    } catch {
                      setTestEmailState("error");
                    }
                  }}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                >
                  {testEmailState === "sending" ? "Sending…" : "Send 3 test emails"}
                </button>
                <span className="text-xs text-muted-foreground">
                  {testEmailState === "sent"
                    ? "Sent — check the founder inbox."
                    : testEmailState === "error"
                      ? "Send failed — check server logs."
                      : "New account, booking link pending, starter kit order."}
                </span>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Business Type Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {typeBreakdown.map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{type}</span>
                        <div className="flex items-center gap-2">
                          <div className="h-2 bg-primary/20 rounded-full" style={{ width: `${(count / merchants.length) * 150}px` }}>
                            <div className="h-full bg-primary rounded-full" style={{ width: "100%" }} />
                          </div>
                          <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Key Rates</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Onboarding completion" value={`${onboardingRate}%`} sub={`${onboardedCount}/${merchants.length}`} />
                  <Stat label="Scan → Email conversion" value={`${scanToSubmitRate}%`} sub={`${totalEmails}/${totalScans}`} />
                  <Stat label="Avg star rating" value={avgRating} />
                  <Stat label="Redemption rate" value={`${redemptionRate}%`} sub={`${totalRedemptions}/${totalFeedback}`} />
                  <Stat label="Est. Revenue (USD)" value={`$${estRevenue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`} sub="≈ approximate" />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Geo */}
          <TabsContent value="geo">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Globe className="size-4" /> Scans by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {countryMap.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No geo data yet — scans will appear here.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {countryMap.map(([code, count]) => (
                        <div key={code} className="flex items-center justify-between">
                          <span className="text-sm font-mono">{code}</span>
                          <div className="flex items-center gap-2">
                            <div className="h-2 bg-primary/20 rounded-full" style={{ width: `${Math.min((count / (countryMap[0]?.[1] || 1)) * 120, 120)}px` }}>
                              <div className="h-full bg-primary rounded-full" />
                            </div>
                            <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top Cities</CardTitle>
                </CardHeader>
                <CardContent>
                  {cityMap.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No city data yet.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {cityMap.map(([city, count]) => (
                        <div key={city} className="flex items-center justify-between">
                          <span className="text-sm">{city}</span>
                          <span className="text-xs text-muted-foreground">{count}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Activation */}
          <TabsContent value="activation">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Print Intent</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Poster downloads (A4)" value={posterDownloads} />
                  <Stat label="Business card downloads" value={cardDownloads} />
                  <Stat label="Total print actions" value={posterDownloads + cardDownloads} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Clock className="size-4" /> Avg Time to Activate
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{avgTimeToActivate.label}</p>
                  <p className="text-xs text-muted-foreground">
                    From onboarding → first scan ({avgTimeToActivate.count} merchant{avgTimeToActivate.count !== 1 ? "s" : ""})
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Dormant Merchants</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold">{dormantMerchants.length}</p>
                  <p className="text-xs text-muted-foreground">Completed onboarding but 0 scans</p>
                  {dormantMerchants.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {dormantMerchants.slice(0, 5).map((m: any) => (
                        <p key={m.id} className="text-xs">{m.business_name} <span className="text-muted-foreground">({m.slug})</span></p>
                      ))}
                      {dormantMerchants.length > 5 && (
                        <p className="text-xs text-muted-foreground">+{dormantMerchants.length - 5} more</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Customer Funnel */}
          <TabsContent value="customer">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Conversion</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Scan → Email" value={`${scanToSubmitRate}%`} />
                  <Stat label="Cooldown hits" value={events.filter((e: any) => e.event_type === "cooldown_hit").length} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Rating</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Average" value={avgRating} />
                  <Stat label="Total feedback" value={totalFeedback} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Feedback Length</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Long (≥20 chars)" value={longFeedback} />
                  <Stat label="Short (<20 chars)" value={shortFeedback} />
                  <Stat label="No comment" value={feedback.filter((f: any) => !f.customer_comment).length} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Reward Loop */}
          <TabsContent value="rewards">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Redemption</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Codes issued" value={totalFeedback} />
                  <Stat label="Codes redeemed" value={totalRedemptions} />
                  <Stat label="Redemption rate" value={`${redemptionRate}%`} />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Code Cascade</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Multi-code customers" value={codeCascade.multiCodeCustomers} />
                  <Stat label="Redeemed C2 without C1" value={codeCascade.c2WithoutC1} />
                  <Stat
                    label="Cascade rate"
                    value={codeCascade.multiCodeCustomers ? `${((codeCascade.c2WithoutC1 / codeCascade.multiCodeCustomers) * 100).toFixed(1)}%` : "—"}
                    sub={codeCascade.multiCodeCustomers ? `${codeCascade.c2WithoutC1}/${codeCascade.multiCodeCustomers}` : undefined}
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm">Nudge Performance</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  <Stat label="Nudges sent" value={events.filter((e: any) => e.event_type === "nudge_sent").length} />
                  <Stat label="Nudge clicks" value={events.filter((e: any) => e.event_type === "nudge_clicked").length} />
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Merchants Table */}
          <TabsContent value="merchants">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">All Merchants</CardTitle>
                  <div className="relative w-56">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="h-7 text-xs pl-7"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Business</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs text-right">Scans</TableHead>
                      <TableHead className="text-xs text-right">Emails</TableHead>
                      <TableHead className="text-xs text-right">Redemption</TableHead>
                      <TableHead className="text-xs text-right">Avg ★</TableHead>
                      <TableHead className="text-xs text-right">Discount</TableHead>
                      <TableHead className="text-xs">Signed Up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMerchants.map((m: any) => {
                      const mFeedback = merchantStats.feedbackByMerchant[m.id] || [];
                      const mScans = merchantStats.scanCounts[m.id] || 0;
                      const mEmails = events.filter((e: any) => e.event_type === "email_submitted" && e.merchant_id === m.id).length;
                      const mRedeemed = mFeedback.filter((f: any) => f.status === "redeemed").length;
                      const mRate = mFeedback.length ? ((mRedeemed / mFeedback.length) * 100).toFixed(0) : "—";
                      const mAvg = mFeedback.length
                        ? (mFeedback.reduce((s: number, f: any) => s + f.star_rating, 0) / mFeedback.length).toFixed(1)
                        : "—";
                      return (
                        <TableRow key={m.id}>
                          <TableCell className="text-xs font-medium">{m.business_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-[10px] capitalize">{m.business_type || "—"}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-right">{mScans}</TableCell>
                          <TableCell className="text-xs text-right">{mEmails}</TableCell>
                          <TableCell className="text-xs text-right">{mRate}%</TableCell>
                          <TableCell className="text-xs text-right">{mAvg}</TableCell>
                          <TableCell className="text-xs text-right">{m.promo_discount_percent}%</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function KPICard({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10 text-primary">{icon}</div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="text-right">
        <span className="text-sm font-semibold">{value}</span>
        {sub && <span className="text-[10px] text-muted-foreground ml-1">({sub})</span>}
      </div>
    </div>
  );
}

function SupportInboxChecklist() {
  const autoReply = `Thanks for reaching Tack — we read every email. You'll hear back within 24h on weekdays (often much sooner). – The Tack team`;
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <LifeBuoy className="size-4" /> hello@tack.company — setup checklist
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm">
          <div>
            <h3 className="font-semibold mb-1.5">1. Create the inbox</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Provision <span className="font-mono">hello@tack.company</span> on Google Workspace, Fastmail, or Migadu.</li>
              <li>Add a forwarding rule to your personal address so you get push notifications.</li>
              <li>Confirm sending + receiving with a test email from an external account.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-1.5">2. DNS records on tack.company root</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li><span className="font-mono">MX</span> — pointing to the inbox provider (e.g. <span className="font-mono">smtp.google.com</span>).</li>
              <li><span className="font-mono">SPF</span> — <span className="font-mono">v=spf1 include:_spf.google.com ~all</span> (or provider equivalent).</li>
              <li><span className="font-mono">DKIM</span> — TXT record from the provider's admin panel.</li>
              <li><span className="font-mono">DMARC</span> — <span className="font-mono">v=DMARC1; p=quarantine; rua=mailto:hello@tack.company</span>.</li>
              <li>The <span className="font-mono">notify.tack.company</span> subdomain (Resend) is separate — don't touch it.</li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-1.5">3. Auto-reply (in your inbox provider, not in code)</h3>
            <p className="text-muted-foreground mb-2">
              Set this as an always-on vacation responder / rule. One reply per sender per 24h.
            </p>
            <pre className="bg-muted/50 border border-border rounded-md p-3 text-xs whitespace-pre-wrap font-mono">{autoReply}</pre>
          </div>

          <div>
            <h3 className="font-semibold mb-1.5">4. Verify it's live</h3>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Send a test from a personal address → confirm it arrives + auto-reply fires within 30s.</li>
              <li>Click the in-app Help button (bottom-right) → "Email support" → confirm prefilled subject + merchant ID land in your inbox.</li>
              <li>Run <span className="font-mono">mail-tester.com</span> on a sent reply — should score 9+ /10.</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
